import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Button,
  Stack,
  Tooltip,
  Dialog,
  DialogContent,
  TablePagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  Print as PrintIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import { useApiQuery } from "../../hooks/useApi";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import api from "../../utils/api";

// Printable Receipt Component
const PrintableReceipt = React.forwardRef(({ receipt, client }, ref) => (
  <div
    ref={ref}
    style={{
      padding: "40px",
      width: "420px",
      margin: "0 auto",
      fontFamily: "monospace",
    }}
  >
    <style>{`
      @media print {
        @page {
          size: A5;
          margin: 10mm;
        }
      }
    `}</style>

    <h2 style={{textAlign: "center"}}>Fiscale</h2>
    <p style={{fontSize:"12px",textAlign: "center", marginBottom: "20px" }}>Koach Gaon, Biswanath Chariali, Assam, 784176, +91 6901945275</p>

    <p><strong>Receipt No:</strong> {receipt?.receiptNumber}</p>
    <p><strong>Date:</strong> {new Date(receipt?.collectedDate || Date.now()).toLocaleDateString()}</p>
    <p><strong>Amount:</strong> ₹{receipt?.amount}</p>
    <p><strong>Client Name:</strong> {client?.name}</p>
    <p><strong>Phone Number:</strong> {client?.phoneNumber}</p>
    <p><strong>Mode of Payment:</strong> {receipt?.paymentMethod}</p>
    {/* {receipt?.bank && <p><strong>Bank:</strong> {receipt.bank}</p>} */}
    {receipt?.transactionReference && (
      <p><strong>Transaction Ref:</strong> {receipt.transactionReference}</p>
    )}
    <p><strong>Collected By:</strong> {receipt?.collectedBy?.name || "—"}</p>
    <p style={{fontSize:"10px"}}>Received with thanks an amount of Rs. {receipt?.amount}/- (One Thousand Five Hundred only) from {client?.name}. Cheques are subject to realisation.</p>
    <hr style={{ margin: "20px 0" }} />

    <p style={{ textAlign: "center", opacity: 0.7 }}>Thank you for choosing Fiscale</p>
  </div>
));

function AllReceiptsPage() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(60);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const receiptRef = useRef();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    params.set("limit", String(rowsPerPage));
    if (debouncedSearch) {
      params.set("search", debouncedSearch);
    }
    return params.toString();
  }, [page, rowsPerPage, debouncedSearch]);

  const { data: response, isLoading, error, refetch } = useApiQuery(
    ["receipts", queryParams],
    `/receipts?${queryParams}`
  );

  const receiptsList = response?.receipts || [];
  const totalReceipts = response?.total || 0;

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `Receipt_${selectedReceipt?.receiptNumber || "Unknown"}`,
    onAfterPrint: () => {
      setPrintDialogOpen(false);
      setSelectedReceipt(null);
    },
  });

  const openPrintDialog = (receipt) => {
    setSelectedReceipt(receipt);
    setPrintDialogOpen(true);
  };

  const filteredReceipts = receiptsList;

  const getPaymentMethodColor = (method) => {
    const methodColors = {
      cash: "primary",
      bank_transfer: "success",
      cheque: "info",
      upi: "secondary",
      card: "warning",
    };
    return methodColors[method?.toLowerCase()] || "default";
  };

  const getClientName = (receipt) => {
    if (typeof receipt.clientId === "object" && receipt.clientId?.name) {
      return receipt.clientId.name;
    }
    return receipt.clientName || "Unknown Client";
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleExportReceiptsToExcel = async () => {
    try {
      setIsExporting(true);
      const url = debouncedSearch
        ? `/receipts?export=true&search=${encodeURIComponent(debouncedSearch)}`
        : `/receipts?export=true`;

      const res = await api.get(url);
      const receiptsToExport = Array.isArray(res.data) ? res.data : (res.data?.receipts || []);

      if (!receiptsToExport || receiptsToExport.length === 0) {
        alert("No receipts found to export.");
        return;
      }

      const excelRows = receiptsToExport.map((receipt) => {
        const clientName = typeof receipt.clientId === "object" && receipt.clientId?.name
          ? receipt.clientId.name
          : receipt.clientName || "Unknown Client";

        const editHistoryFormatted = Array.isArray(receipt.editHistory) && receipt.editHistory.length > 0
          ? receipt.editHistory
              .map((edit, idx) => {
                const dateStr = edit.editedAt ? new Date(edit.editedAt).toLocaleString("en-IN") : "";
                const comment = edit.comment || edit.editComment || "";
                return `[Edit ${idx + 1} on ${dateStr}]: ${comment}`;
              })
              .join("\n")
          : "No edit history";

        return {
          "Receipt No.": receipt.receiptNumber || receipt.receiptId || "N/A",
          "Client": clientName,
          "Amount": receipt.amount ?? 0,
          "Payment Method": (receipt.paymentMethod || "N/A").toUpperCase(),
          "Bank": receipt.bank || "N/A",
          "Date": receipt.collectedDate ? new Date(receipt.collectedDate).toLocaleDateString("en-IN") : "N/A",
          "Edit History": editHistoryFormatted,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Receipts");

      const todayStr = new Date().toISOString().split("T")[0];
      XLSX.writeFile(workbook, `Fiscale_Fee_Receipts_${todayStr}.xlsx`);
    } catch (err) {
      console.error("Export error:", err);
      alert("Failed to export receipts.");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading receipts: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            All Fee Receipts
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Search and manage all fee receipts
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => refetch()} color="primary">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="primary"
            startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            onClick={handleExportReceiptsToExcel}
            disabled={isExporting || receiptsList.length === 0}
          >
            {isExporting ? "Exporting..." : "Export to Excel"}
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            fullWidth
            placeholder="Search by Receipt Number, Receipt ID, Client Name, Phone, Amount, Payment Method, or Bank..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" color="textSecondary">
            {totalReceipts} receipt{totalReceipts !== 1 ? "s" : ""} found
            {debouncedSearch && ` for "${debouncedSearch}"`}
          </Typography>
          {searchTerm && (
            <Button size="small" onClick={handleClearSearch}>
              Clear Search
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {filteredReceipts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <ReceiptIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {debouncedSearch ? "No receipts found" : "No receipts available"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {debouncedSearch
                ? "Try adjusting your search criteria"
                : "Receipts will appear here once they are created"}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Receipt No.</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReceipts.map((receipt) => (
                  <TableRow key={receipt._id || receipt.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                        {receipt.receiptNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {getClientName(receipt)}
                      </Typography>
                      {receipt.clientId?.phoneNumber && (
                        <Typography variant="caption" color="textSecondary">
                          {receipt.clientId.phoneNumber}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        ₹{(receipt.amount || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={receipt.paymentMethod?.toUpperCase()}
                        color={getPaymentMethodColor(receipt.paymentMethod)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {receipt.bank ? (
                        <Chip
                          label={receipt.bank}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(receipt.collectedDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(receipt.collectedDate).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Print Receipt">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => openPrintDialog(receipt)}
                        >
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {filteredReceipts.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[60, 100, 200]}
            component="div"
            count={totalReceipts}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </Paper>

      {/* Print Dialog */}
      <Dialog
        open={printDialogOpen}
        onClose={() => {
          setPrintDialogOpen(false);
          setSelectedReceipt(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Receipt Preview</Typography>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Print
            </Button>
          </Box>
          {selectedReceipt && (
            <PrintableReceipt
              ref={receiptRef}
              receipt={selectedReceipt}
              client={selectedReceipt.clientId}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default AllReceiptsPage;